import React, { useState, useCallback, useEffect } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import api from '../../../utils/api'
import { API_AUTOCOMPLETE_PAGE_SIZE } from '../../../consts'
import { stableSerializeRecord } from '../../../utils/stableSerializeRecord'
import MultiSelectWithCheckBox, { RenderTagsType, type MultiSelectWithCheckBoxProps } from '../MultiSelectWithCheckBox'

interface MultiSelectApiAutoCompleteProps<T> extends Omit<MultiSelectWithCheckBoxProps<T>, 'getOptionLabel' | 'getOptionValue' | 'loading' | 'onChange' | 'options'> {
  apiEndpoint: string
  getOptionLabel: (option: T) => string
  getOptionSubLabel?: (option: T) => string
  getOptionValue: (option: T) => string
  getOptionDisabled?: (option: T) => boolean
  sortField?: string
  name: string
  apiExtraParams?: Record<string, any>
  label?: string
  skip?: boolean
  staleTime?: number
  onOptionsLoaded?: (options: T[]) => void
  passedData?: T[]
  noSortAndFilter?: boolean
}

export const MultiSelectApiAutoComplete = <T,>({
  apiEndpoint,
  getOptionLabel,
  getOptionSubLabel,
  getOptionValue,
  getOptionDisabled,
  name,
  apiExtraParams,
  label,
  sortField,
  skip = false,
  staleTime = 0,
  onOptionsLoaded,
  passedData,
  noSortAndFilter = false,
  ...props
}: MultiSelectApiAutoCompleteProps<T>): JSX.Element => {
  const { placeholder, disabled } = props
  const [input, setInput] = useState<string>('')

  const sortFieldUnderscore = reverseCamelCase(sortField ?? '')

  const { data = [], isLoading, isFetching, isPending } = useQuery<T[]>({
    queryKey: [apiEndpoint, input, stableSerializeRecord(apiExtraParams as Record<string, unknown>), skip, sortFieldUnderscore],
    placeholderData: keepPreviousData,
    enabled: !skip && !!apiEndpoint,
    staleTime,

    queryFn: async (): Promise<T[]> => {
      const params: Record<string, any> = {
        pageSize: API_AUTOCOMPLETE_PAGE_SIZE,
        search: input,
        ...apiExtraParams
      }
      if (sortField) {
        params.sort = JSON.stringify({
          field: sortFieldUnderscore,
          direction: 'asc'
        })
      }

      const response = await api.get(apiEndpoint, { params })
      return response.data
    }

  })

  useEffect(() => {
    if (!skip && data?.length) {
      onOptionsLoaded?.(data)
    }
  }, [data, skip, onOptionsLoaded])

  const handleInputChange = useCallback((text: string) => {
    setInput(text)
  }, [])

  const handleOptionSelected = useCallback(() => {
    setInput('')
  }, [])
  const activeOptions = skip ? (passedData ?? []) : (data ?? [])
  const hasOptions = activeOptions.length > 0

  return (
    <MultiSelectWithCheckBox
      label={label}
      name={name}
      options={activeOptions}
      placeholder={hasOptions ? placeholder : 'No options found'}
      getOptionLabel={(option: T) => getOptionLabel(option)}
      getOptionValue={(option: T) => getOptionValue(option)}
      getOptionSubLabel={(option: T) => getOptionSubLabel?.(option) ?? ''}
      getOptionDisabled={getOptionDisabled}
      loading={(isLoading || isFetching || isPending) && !disabled && !skip}
      fullWidth
      onInputChange={handleInputChange}
      disabled={disabled}
      onOptionSelected={handleOptionSelected}
      showSelectAll
      {...props}
    />
  )
}

const reverseCamelCase = (str: string): string =>
  str.replace(/([A-Z])/g, '_$1').toLowerCase()

export default MultiSelectApiAutoComplete
